"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Field,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SMALL,
  CARD,
  INPUT_MONO,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  ALL_WEEKDAYS,
  buildDateSpec,
  countAllowedDays,
  countOutcomes,
  DATE_FORMATS,
  type DateFormat,
  type DateOrder,
  type DateProblem,
  type DrawnDate,
  drawDates,
  formatDrawn,
  MAX_COUNT,
  WEEKEND_WEEKDAYS,
  type Weekday,
  WORK_WEEKDAYS,
  weekdayName,
} from "@/lib/tool-kit/random-fun/random-date";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_DATE_GENERATOR_STRINGS = {
  rangeHeading: "Date range",
  startLabel: "Earliest date",
  endLabel: "Latest date",
  weekdaysLabel: "Allowed days of the week",
  presetEvery: "Every day",
  presetWork: "Weekdays (Monday to Friday)",
  presetWeekend: "Weekends only",
  timeHeading: "Time of day",
  includeTime: "Add a random time to each date",
  fromLabel: "Earliest time",
  toLabel: "Latest time",
  stepLabel: "Times to choose from",
  step1: "Every minute",
  step5: "Every 5 minutes",
  step15: "Every 15 minutes",
  step30: "Every 30 minutes",
  step60: "On the hour",
  outputHeading: "Output",
  countLabel: "How many (1–1,000)",
  uniqueLabel: "No repeats",
  orderLabel: "Order",
  orderRandom: "As drawn",
  orderAscending: "Earliest first",
  orderDescending: "Latest first",
  formatLabel: "Format",
  formatIso: "ISO 8601 (2026-03-14)",
  formatLong: "Full, with weekday",
  formatMedium: "Medium (Mar 14, 2026)",
  formatUs: "MM/DD/YYYY",
  formatDayFirst: "DD/MM/YYYY",
  hour12Label: "Use a 12-hour clock",
  generate: "Generate dates",
  results: "Your dates",
  announce: "Generated {count}: first {first}.",
  placeholder: "Press the button to draw dates.",
  copyAll: "Copy all",
  copied: "Copied!",
  statDays: "Allowed days in range",
  statOutcomes: "Possible results",
  problemStart: "Enter a real earliest date.",
  problemEnd: "Enter a real latest date.",
  problemOrder: "The earliest date must not come after the latest date.",
  problemWeekdays: "Choose at least one day of the week.",
  problemFrom: "Enter the earliest time as hours and minutes.",
  problemTo: "Enter the latest time as hours and minutes.",
  problemTimeOrder: "The earliest time must not come after the latest time.",
  problemEmpty:
    "No day in this range falls on an allowed day of the week. Widen the range or allow more days.",
  problemCount: "Enter how many dates you want, from 1 to 1,000.",
  problemTooMany:
    "This range holds only {available} possible results, so it cannot give {count} different ones. Lower the number, widen the range or allow repeats.",
} as const;

type Strings = typeof RANDOM_DATE_GENERATOR_STRINGS;

const PROBLEM_KEY: Record<DateProblem, keyof Strings> = {
  start: "problemStart",
  end: "problemEnd",
  order: "problemOrder",
  weekdays: "problemWeekdays",
  from: "problemFrom",
  to: "problemTo",
  timeOrder: "problemTimeOrder",
  empty: "problemEmpty",
};

const FORMAT_KEY: Record<DateFormat, keyof Strings> = {
  iso: "formatIso",
  long: "formatLong",
  medium: "formatMedium",
  us: "formatUs",
  dayFirst: "formatDayFirst",
};

const STEPS = [
  { minutes: 1, key: "step1" },
  { minutes: 5, key: "step5" },
  { minutes: 15, key: "step15" },
  { minutes: 30, key: "step30" },
  { minutes: 60, key: "step60" },
] as const;

/** Monday first, the order most calendars outside the United States use. */
const WEEK_ORDER: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 0];

export default function RandomDateGeneratorWidget() {
  const strings = useToolUi(RANDOM_DATE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [start, setStart] = useState("2026-01-01");
  const [end, setEnd] = useState("2026-12-31");
  const [weekdays, setWeekdays] = useState<readonly Weekday[]>(ALL_WEEKDAYS);
  const [useTime, setUseTime] = useState(false);
  const [from, setFrom] = useState("09:00");
  const [to, setTo] = useState("17:00");
  const [step, setStep] = useState("15");
  const [countText, setCountText] = useState("10");
  const [unique, setUnique] = useState(true);
  const [order, setOrder] = useState<DateOrder>("random");
  const [format, setFormat] = useState<DateFormat>("iso");
  const [hour12, setHour12] = useState(false);
  const [drawn, setDrawn] = useState<DrawnDate[] | null>(null);
  const [error, setError] = useState("");
  const [announcement, setAnnouncement] = useState("");

  const built = useMemo(
    () =>
      buildDateSpec({
        start,
        end,
        weekdays,
        useTime,
        from,
        to,
        stepMinutes: Number(step),
      }),
    [start, end, weekdays, useTime, from, to, step]
  );

  function toggleWeekday(day: Weekday, on: boolean) {
    setWeekdays(
      ALL_WEEKDAYS.filter((d) => (d === day ? on : weekdays.includes(d)))
    );
  }

  function formatted(list: readonly DrawnDate[]): string[] {
    return list.map((item) => formatDrawn(item, format, locale, hour12));
  }

  function generate() {
    if ("problem" in built) {
      setError(strings[PROBLEM_KEY[built.problem]]);
      return;
    }
    const count = parseIntegerInput(countText);
    if (count === null || count < 1 || count > MAX_COUNT) {
      setError(strings.problemCount);
      return;
    }
    const result = drawDates(built.spec, { count, unique, order });
    if (result === null) {
      setError(
        t(strings.problemTooMany, {
          available: fmt(countOutcomes(built.spec)),
          count: fmt(count),
        })
      );
      return;
    }
    setError("");
    setDrawn(result);
    const lines = formatted(result);
    setAnnouncement(
      t(strings.announce, { count: fmt(lines.length), first: lines[0] ?? "" })
    );
  }

  const lines = drawn ? formatted(drawn) : [];
  const days = "spec" in built ? countAllowedDays(built.spec) : null;
  const outcomes = "spec" in built ? countOutcomes(built.spec) : null;

  return (
    <div className="space-y-6">
      <fieldset className="space-y-4">
        <legend className="sr-only">{strings.rangeHeading}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="rdg-start"
            label={strings.startLabel}
            onChange={setStart}
            placeholder="2026-01-01"
            type="date"
            value={start}
          />
          <TextField
            id="rdg-end"
            label={strings.endLabel}
            onChange={setEnd}
            placeholder="2026-12-31"
            type="date"
            value={end}
          />
        </div>
        <fieldset className="space-y-3">
          <legend className="font-medium text-foreground text-sm">
            {strings.weekdaysLabel}
          </legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {WEEK_ORDER.map((day) => (
              <Checkbox
                checked={weekdays.includes(day)}
                key={day}
                label={weekdayName(day, locale, "long")}
                onChange={(on) => toggleWeekday(day, on)}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SMALL}
              onClick={() => setWeekdays(ALL_WEEKDAYS)}
              type="button"
            >
              {strings.presetEvery}
            </button>
            <button
              className={BUTTON_SMALL}
              onClick={() => setWeekdays(WORK_WEEKDAYS)}
              type="button"
            >
              {strings.presetWork}
            </button>
            <button
              className={BUTTON_SMALL}
              onClick={() => setWeekdays(WEEKEND_WEEKDAYS)}
              type="button"
            >
              {strings.presetWeekend}
            </button>
          </div>
        </fieldset>
      </fieldset>

      <fieldset className={`${CARD} space-y-4`}>
        <legend className="sr-only">{strings.timeHeading}</legend>
        <Checkbox
          checked={useTime}
          label={strings.includeTime}
          onChange={setUseTime}
        />
        {useTime ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              id="rdg-from"
              label={strings.fromLabel}
              onChange={setFrom}
              type="time"
              value={from}
            />
            <TextField
              id="rdg-to"
              label={strings.toLabel}
              onChange={setTo}
              type="time"
              value={to}
            />
            <SelectField
              id="rdg-step"
              label={strings.stepLabel}
              onChange={setStep}
              options={STEPS.map((s) => ({
                value: String(s.minutes),
                label: strings[s.key],
              }))}
              value={step}
            />
          </div>
        ) : null}
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="sr-only">{strings.outputHeading}</legend>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field id="rdg-count" label={strings.countLabel}>
            <input
              className={INPUT_MONO}
              id="rdg-count"
              inputMode="numeric"
              onChange={(event) => setCountText(event.target.value)}
              type="text"
              value={countText}
            />
          </Field>
          <SelectField
            id="rdg-order"
            label={strings.orderLabel}
            onChange={setOrder}
            options={[
              { value: "random", label: strings.orderRandom },
              { value: "ascending", label: strings.orderAscending },
              { value: "descending", label: strings.orderDescending },
            ]}
            value={order}
          />
          <SelectField
            id="rdg-format"
            label={strings.formatLabel}
            onChange={setFormat}
            options={DATE_FORMATS.map((value) => ({
              value,
              label: strings[FORMAT_KEY[value]],
            }))}
            value={format}
          />
          <div className="flex flex-col justify-end gap-2">
            <Checkbox
              checked={unique}
              label={strings.uniqueLabel}
              onChange={setUnique}
            />
            {useTime ? (
              <Checkbox
                checked={hour12}
                label={strings.hour12Label}
                onChange={setHour12}
              />
            ) : null}
          </div>
        </div>
      </fieldset>

      {days !== null && outcomes !== null ? (
        <StatGrid>
          <StatCard label={strings.statDays} value={fmt(days)} />
          <StatCard label={strings.statOutcomes} value={fmt(outcomes)} />
        </StatGrid>
      ) : null}

      <button className={BUTTON_PRIMARY} onClick={generate} type="button">
        {strings.generate}
      </button>

      {error ? <ErrorNotice>{error}</ErrorNotice> : null}

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>
        {lines.length === 0 ? (
          <Notice>{strings.placeholder}</Notice>
        ) : (
          <>
            <ol className="max-h-96 list-decimal space-y-1 overflow-y-auto rounded-lg border border-border bg-card py-3 pr-4 pl-10 text-foreground text-sm">
              {lines.map((line, index) => (
                // Draws can repeat a value when repeats are allowed.
                <li key={`${index}-${line}`}>{line}</li>
              ))}
            </ol>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyAll}
              text={lines.join("\n")}
            />
          </>
        )}
      </section>
    </div>
  );
}
